'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import {
	Snowflake,
	Thermometer,
	Package,
	Truck,
	BarChart3,
	Clock,
	CheckCircle2,
	ArrowRight,
} from 'lucide-react';
import { Button } from './components/ui/button';

const capabilities = [
	{
		title: 'Freezer Storage',
		description: 'Maintain products at -10°F and below for frozen food items.',
		temperature: '-10°F and below',
		icon: Snowflake,
		color: 'bg-blue-50 border-blue-200',
		iconColor: 'text-blue-600',
	},
	{
		title: 'Refrigerated Storage',
		description: 'Temperature-controlled environment for fresh and chilled products.',
		temperature: '32°F to 50°F',
		icon: Thermometer,
		color: 'bg-green-50 border-green-200',
		iconColor: 'text-green-600',
	},
	{
		title: 'Dry Storage',
		description: 'Climate-controlled ambient storage for shelf-stable products.',
		temperature: '50°F to 70°F',
		icon: Package,
		color: 'bg-orange-50 border-orange-200',
		iconColor: 'text-orange-600',
	},
];

const services = [
	'Cross-docking services',
	'Pick and pack operations',
	'Inventory management',
	'Order fulfillment',
	'Quality control inspections',
	'Lot tracking and tracing',
	'Temperature monitoring',
	'Sanitation protocols',
];

export function FoodGradeCapabilities() {
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0.05,
		rootMargin: '300px 0px',
	});

	return (
		<section className="py-24 bg-gray-50">
			<div className="container mx-auto px-4 md:px-6">
				<motion.div
					ref={ref}
					initial="hidden"
					animate={inView ? 'show' : 'hidden'}
					variants={fadeIn('up')}
					className="text-center mb-16"
				>
					<h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
						Storage Capabilities
					</h2>
					<h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
						Multi-Temperature Food Storage Solutions
					</h3>
					<p className="max-w-2xl mx-auto text-gray-700">
						Our food grade warehouse offers comprehensive storage solutions across
						multiple temperature zones to meet all your food product requirements.
					</p>
				</motion.div>

				<motion.div
					ref={ref}
					initial="hidden"
					animate={inView ? 'show' : 'hidden'}
					variants={fadeIn('up')}
					className="grid md:grid-cols-3 gap-8 mb-16"
				>
					{capabilities.map((capability, index) => (
						<motion.div
							key={index}
							variants={fadeIn('up', 0.1 * index)}
							className={`${capability.color} border-2 rounded-xl p-8 hover:shadow-lg transition-all duration-300 group`}
						>
							<div
								className={`w-16 h-16 ${capability.color.replace(
									'50',
									'100'
								)} rounded-xl flex items-center justify-center ${
									capability.iconColor
								} mb-6 group-hover:scale-110 transition-transform`}
							>
								<capability.icon size={32} />
							</div>

							<h4 className="text-xl font-bold text-gray-900 mb-3">
								{capability.title}
							</h4>

							<p className="text-gray-700 mb-4 leading-relaxed">
								{capability.description}
							</p>

							<div className="bg-white/80 rounded-lg p-3 text-center">
								<span className="text-sm font-semibold text-gray-600">
									Temperature Range
								</span>
								<div className={`text-lg font-bold ${capability.iconColor}`}>
									{capability.temperature}
								</div>
							</div>
						</motion.div>
					))}
				</motion.div>

				<div className="grid lg:grid-cols-2 gap-12 items-center mt-24">
					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('right')}
					>
						<h3 className="text-2xl font-bold text-gray-900 mb-6">
							Comprehensive Food Grade Services
						</h3>

						<p className="text-lg text-gray-700 mb-8 leading-relaxed">
							Beyond storage, we offer a full range of value-added services designed
							specifically for food products. Our trained staff and specialized
							equipment ensure your products maintain their quality and safety
							throughout the supply chain.
						</p>

						<div className="grid grid-cols-2 gap-4 mb-8">
							{services.map((service, index) => (
								<div key={index} className="flex items-center gap-3">
									<CheckCircle2
										className="text-brand-600 flex-shrink-0"
										size={16}
									/>
									<span className="text-gray-700 text-sm">{service}</span>
								</div>
							))}
						</div>

						<Button
							asChild
							className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-6 text-lg"
						>
							<a href="#contact">
								Get Started Today
								<ArrowRight className="ml-2" size={20} />
							</a>
						</Button>
					</motion.div>

					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('left')}
						className="relative"
					>
						<div className="absolute -top-6 -left-6 w-32 h-32 bg-brand-600 rounded-tl-2xl"></div>
						<img
							src="https://lindner-marketing.s3.eu-west-2.amazonaws.com/two-workers-inspecting-apples-in-distribution-ware-2024-09-17-05-42-54-utc-2.webp"
							alt="Workers inspecting food products in distribution warehouse"
							className="rounded-lg shadow-xl relative z-10 w-full h-[400px] object-cover"
						/>
						<div className="absolute -bottom-6 -right-6 w-40 h-40 bg-brand-100 rounded-br-2xl"></div>

						{/* Stats overlay */}
						<div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-lg p-6 z-20">
							<div className="grid grid-cols-2 gap-4 text-center">
								<div>
									<div className="text-2xl font-bold text-brand-600">24/7</div>
									<div className="text-sm text-gray-600">
										Temperature Monitoring
									</div>
								</div>
								<div>
									<div className="text-2xl font-bold text-brand-600">100%</div>
									<div className="text-sm text-gray-600">FDA Compliant</div>
								</div>
							</div>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
